import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none",
  {
    variants: {
      variant: {
        default: "bg-primary-700 text-white hover:bg-primary-600 shadow-sm",
        destructive:
          "bg-red-700 text-white hover:bg-red-800 shadow-sm",
        
        // --- Primary Variants ---
        primary: "bg-primary-700 text-white hover:bg-primary-600 shadow-sm",
        "outline-primary":
          "border border-primary-700 bg-transparent text-primary-700 hover:bg-primary-50",
        "ghost-primary": "text-primary-700 hover:bg-primary-50",

        // --- Secondary Variants ---
        secondary:
          "bg-secondary-700 text-white hover:bg-secondary-800 shadow-sm",
        "outline-secondary":
          "border border-secondary-700 bg-transparent text-secondary-700 hover:bg-secondary-50",
        "ghost-secondary": "text-secondary-700 hover:bg-secondary-50",

        // --- Accent Variants ---
        accent:
          "bg-[#c2410c] text-white border border-[#7a2b00]/80 shadow-lg shadow-[#c2410c]/25 hover:bg-[#9a2f0c] hover:text-white",
        "outline-accent":
          "border border-accent-400/70 bg-transparent text-white hover:bg-accent-500/15 hover:text-white hover:border-accent-300",
        "ghost-accent": "text-white hover:text-white hover:bg-white/10",

        // --- CTA Variants (solid/outline) ---
        cta:
          "bg-[#c2410c] text-white shadow-lg shadow-[#c2410c]/25 border border-[#7a2b00]/80 hover:bg-[#9a2f0c] hover:text-white hover:shadow-[#c2410c]/35",
        "cta-outline":
          "border border-white/50 bg-white/5 text-white shadow-[0_0_0_1px_rgba(255,255,255,0.08)] hover:border-white/70 hover:bg-white/15 hover:text-white",

        // --- Other Variants ---
        link: "text-primary-700 underline-offset-4 hover:underline",
        subtle: "bg-noir-100 text-noir-700 hover:bg-noir-200",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-sm",
        lg: "h-12 px-6 py-3 text-base",
        xl: "h-14 px-8 py-4 text-lg",
        icon: "h-10 w-10 p-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
